import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { config } from '../../config';

type ResultStatus = {
  orderId?: string;
  orderNumber?: string | null;
  isTempOrder?: boolean;
  paymentStatus?: string;
  dibsyStatus?: string | null;
  amount?: number;
  paymentUrl?: string | null;
  error?: string;
  code?: string;
};

const PartnerPaymentResult = () => {
  const [params] = useSearchParams();
  const orderId = String(params.get('orderId') || '').trim();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ResultStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const load = async () => {
      if (!orderId) {
        setError('Missing orderId');
        setLoading(false);
        return;
      }
      try {
        const base = String(config.BASE_URL || '').replace(/\/+$/, '');
        const url = `${base}/v1/partner/payment/result-status?orderId=${encodeURIComponent(
          orderId
        )}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        const json = (await res.json().catch(() => null)) as ResultStatus | null;
        if (cancelled) return;
        if (!res.ok) {
          setError(json?.error || json?.code || `Request failed (${res.status})`);
          setLoading(false);
          return;
        }
        setStatus(json);
        const paid =
          json?.paymentStatus === 'paid' ||
          json?.dibsyStatus === 'succeeded' ||
          json?.isTempOrder === false;
        if (!paid && tries < 8) {
          tries += 1;
          setTimeout(load, 2000);
          return;
        }
        setLoading(false);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load payment status');
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const paid =
    status?.paymentStatus === 'paid' ||
    status?.dibsyStatus === 'succeeded' ||
    status?.isTempOrder === false;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 5,
        bgcolor: 'grey.100',
      }}
    >
      <Container maxWidth='sm' disableGutters>
        <Stack alignItems='center' sx={{ mb: 2.5 }}>
          <Box
            component='img'
            src='/static/adori-logo.png'
            alt='Adori'
            sx={{ width: 120, height: 'auto' }}
          />
        </Stack>

        <Card
          sx={{
            p: { xs: '28px 20px 32px', sm: '30px 30px 36px' },
            boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            borderRadius: 2,
          }}
        >
          <Stack spacing={2} alignItems='center' textAlign='center'>
            {loading && (
              <>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'primary.lighter',
                  }}
                >
                  <CircularProgress size={28} color='primary' />
                </Box>
                <Typography variant='h4' color='primary'>
                  Confirming your payment…
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ color: '#637381' }}>
                  Please keep this page open while we verify the payment.
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                    borderRadius: 1,
                    px: 1.75,
                    py: 1.5,
                    typography: 'body2',
                    fontWeight: 500,
                  }}
                >
                  This usually takes a few seconds.
                </Box>
              </>
            )}

            {!loading && error && (
              <>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'error.lighter',
                    color: 'error.dark',
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant='h4' color='error.dark'>
                  Something went wrong
                </Typography>
                <Typography variant='body2' sx={{ color: '#637381' }}>
                  We couldn’t confirm this payment yet.
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    bgcolor: 'error.lighter',
                    color: 'error.dark',
                    borderRadius: 1,
                    px: 1.75,
                    py: 1.5,
                    typography: 'body2',
                    fontWeight: 500,
                  }}
                >
                  Try refreshing the page, or contact Adori support with your order details.
                </Box>
                <Typography variant='caption' color='text.disabled'>
                  {error}
                </Typography>
              </>
            )}

            {!loading && !error && paid && (
              <>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'success.lighter',
                    color: 'success.dark',
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant='h4' color='success.dark'>
                  Payment successful
                </Typography>
                <Typography variant='body2' sx={{ color: '#637381' }}>
                  Your payment was received and the order is confirmed.
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    bgcolor: 'success.lighter',
                    color: 'success.dark',
                    borderRadius: 1,
                    px: 1.75,
                    py: 1.5,
                    typography: 'body2',
                    fontWeight: 500,
                  }}
                >
                  Order placed successfully. You can close this page.
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                    textAlign: 'left',
                  }}
                >
                  {status?.orderNumber && (
                    <>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        sx={{ px: 2, py: 1.75 }}
                      >
                        <Typography variant='body2' sx={{ color: '#637381', fontWeight: 500 }}>
                          Order number
                        </Typography>
                        <Typography variant='subtitle1' fontWeight={700}>
                          {status.orderNumber}
                        </Typography>
                      </Stack>
                      {status?.amount != null && <Divider />}
                    </>
                  )}
                  {status?.amount != null && (
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      sx={{ px: 2, py: 1.75 }}
                    >
                      <Typography variant='body2' sx={{ color: '#637381', fontWeight: 500 }}>
                        Amount paid
                      </Typography>
                      <Typography variant='subtitle1' fontWeight={700}>
                        {Number(status.amount).toFixed(2)} QAR
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Typography variant='caption' color='text.disabled'>
                  Thank you for ordering with Adori.
                </Typography>
              </>
            )}

            {!loading && !error && !paid && (
              <>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'warning.lighter',
                    color: 'warning.dark',
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant='h4' color='warning.dark'>
                  Payment pending
                </Typography>
                <Typography variant='body2' sx={{ color: '#637381' }}>
                  We’re still confirming your payment with the bank.
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    bgcolor: 'warning.lighter',
                    color: 'warning.dark',
                    borderRadius: 1,
                    px: 1.75,
                    py: 1.5,
                    typography: 'body2',
                    fontWeight: 500,
                  }}
                >
                  If you just paid, wait a moment and refresh this page.
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                    textAlign: 'left',
                  }}
                >
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ px: 2, py: 1.75 }}
                  >
                    <Typography variant='body2' sx={{ color: '#637381', fontWeight: 500 }}>
                      Status
                    </Typography>
                    <Typography variant='subtitle1' fontWeight={700}>
                      {status?.dibsyStatus || status?.paymentStatus || 'pending'}
                    </Typography>
                  </Stack>
                </Box>
                <Typography variant='caption' color='text.disabled'>
                  This page will update once payment is confirmed.
                </Typography>
              </>
            )}
          </Stack>
        </Card>
      </Container>
    </Box>
  );
};

export default PartnerPaymentResult;
